import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
  VStack,
  Avatar,
  Input,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FaMapMarkerAlt, FaCalendarAlt, FaEye, FaUser } from "react-icons/fa";
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyClients } from '../services/clientService';
import JalaliDateDisplay from '../components/JalaliDateDisplay';
import toast from 'react-hot-toast';

function MyClients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // دریافت لیست مراجعان مشاور
  const {
    data: clientsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['my-clients', searchTerm],
    () => getMyClients({ search: searchTerm }),
    {
      staleTime: 2 * 60 * 1000, // 2 دقیقه
    }
  );

  const handleViewProfile = (clientId) => {
    navigate(`/client-profile/${clientId}`);
  };

  const handleViewClientDetails = (client) => {
    setSelectedClient(client);
    onOpen();
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'فعال';
      case 'inactive':
        return 'غیرفعال';
      default:
        return 'نامشخص';
    }
  };

  // اگر در حال بارگذاری است
  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری مراجعان...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا وجود دارد
  if (error) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>خطا!</AlertTitle>
              <AlertDescription>
                خطا در بارگذاری مراجعان. لطفاً دوباره تلاش کنید.
              </AlertDescription>
            </Alert>
          </Box>
        </Box>
      </Flex>
    );
  }

  const clients = clientsData?.data?.clients || clientsData?.clients || [];

  return (
    <>
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            
            {/* Header */}
            <Flex justify="space-between" align="center" mb={6}>
              <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  مراجعین من
                </Text>
                <Text fontSize="sm" color="gray.400">
                  لیست تمام مراجعانی که با شما ملاقات داشته‌اند
                </Text>
              </VStack>
              <Button
                colorScheme="teal"
                variant="outline"
                onClick={() => refetch()}
              >
                بروزرسانی
              </Button>
            </Flex>

            {/* جستجو */}
            <Flex justify="space-between" align="center" mb={6}>
              <HStack spacing={4}>
                <InputGroup maxW="400px">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="جستجو در مراجعان..."
                    borderRadius="15px"
                    fontSize="sm"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </InputGroup>
              </HStack>
              <Text fontSize="sm" color="gray.400">
                تعداد کل: {clients.length} مراجع
              </Text>
            </Flex>
            
            {/* جدول مراجعان */}
            <Box overflowX={{ sm: "scroll", xl: "hidden" }}>
              <Table variant="simple" color={textColor} size="sm">
                <Thead>
                  <Tr my=".8rem" pl="0px" color="gray.400">
                    <Th borderColor={borderColor} textAlign="right">
                      مراجع
                    </Th>
                    <Th borderColor={borderColor} textAlign="right">
                      کد ملی
                    </Th>
                    <Th borderColor={borderColor} textAlign="right">
                      شماره پرونده
                    </Th>

                    <Th borderColor={borderColor} textAlign="right">
                      شغل
                    </Th>
                    <Th borderColor={borderColor} textAlign="right">
                      آخرین ملاقات
                    </Th>
                    <Th borderColor={borderColor} textAlign="right">
                      وضعیت
                    </Th>
                    <Th borderColor={borderColor} textAlign="right">
                      عملیات
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {clients.length === 0 ? (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={8}>
                        <VStack spacing={2}>
                          <Text color="gray.500">هیچ مراجعه‌ای یافت نشد</Text>
                          <Text fontSize="sm" color="gray.400">
                            {searchTerm ? 'لطفاً عبارت جستجو را تغییر دهید' : 'هنوز هیچ مراجعه‌ای با شما ملاقات نداشته است'}
                          </Text>
                        </VStack>
                      </Td>
                    </Tr>
                  ) : (
                    clients.map((client) => (
                      <Tr key={client._id || client.id}>
                        <Td minWidth={{ sm: "250px" }} pl="0px">
                          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
                            <Avatar
                              size="md"
                              name={client.firstName && client.lastName 
                                ? `${client.firstName} ${client.lastName}`
                                : client.name || 'نامشخص'
                              }
                              bg="teal.500"
                              me="18px"
                            />
                            <Flex direction="column">
                              <Text
                                fontSize="md"
                                color={textColor}
                                fontWeight="bold"
                                minWidth="100%"
                                cursor="pointer"
                                _hover={{ color: "teal.500" }}
                                onClick={() => handleViewClientDetails(client)}
                              >
                                {client.firstName && client.lastName 
                                  ? `${client.firstName} ${client.lastName}`
                                  : client.name || 'نامشخص'
                                }
                              </Text>
                              <Text fontSize="sm" color="gray.400" fontWeight="normal">
                                کد ملی: {client.nationalId || 'ثبت نشده'}
                              </Text>
                            </Flex>
                          </Flex>
                        </Td>
                        <Td>
                          <Text fontSize="md" color={textColor} fontWeight="bold">
                            {client.nationalId || 'کد ملی ثبت نشده'}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="md" color={textColor} fontWeight="bold">
                            {client.fileNumber || 'شماره پرونده ثبت نشده'}
                          </Text>
                        </Td>

                        <Td>
                          <Text fontSize="md" color={textColor} fontWeight="bold">
                            {client.occupation || 'شغل ثبت نشده'}
                          </Text>
                        </Td>
                        <Td>
                          {client.lastAppointment ? (
                            <VStack align="start" spacing={1}>
                              <HStack spacing={2}>
                                <FaCalendarAlt color="gray.400" size="12px" />
                                <JalaliDateDisplay
                                  date={client.lastAppointment}
                                  format="jYYYY/jMM/jDD"
                                  textProps={{
                                    fontSize: "sm",
                                    color: textColor
                                  }}
                                />
                              </HStack>
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color="gray.400">
                              بدون ملاقات
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Badge
                            bg={`${getStatusColor(client.active !== false ? 'active' : 'inactive')}.400`}
                            color="white"
                            fontSize="16px"
                            p="3px 10px"
                            borderRadius="8px"
                          >
                            {getStatusText(client.active !== false ? 'active' : 'inactive')}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing="2">
                            <Button
                              size="sm"
                              colorScheme="teal"
                              variant="outline"
                              leftIcon={<FaEye />}
                              onClick={() => handleViewProfile(client._id || client.id)}
                            >
                              مشاهده پروفایل
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="gray"
                              variant="outline"
                              leftIcon={<FaUser />}
                              onClick={() => handleViewClientDetails(client)}
                            >
                              جزئیات
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      </Flex>

      {/* Modal جزئیات مراجع */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>جزئیات مراجع</ModalHeader>
          <ModalCloseButton right="auto" left={4} />
          <ModalBody pb={6}>
            {selectedClient && (
              <VStack spacing={4} align="start">
                <Flex align="center" w="100%">
                  <Avatar
                    size="lg"
                    name={selectedClient.firstName && selectedClient.lastName 
                      ? `${selectedClient.firstName} ${selectedClient.lastName}`
                      : selectedClient.name || 'نامشخص'
                    }
                    bg="teal.500"
                    me={4}
                  />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xl" fontWeight="bold">
                      {selectedClient.firstName && selectedClient.lastName 
                        ? `${selectedClient.firstName} ${selectedClient.lastName}`
                        : selectedClient.name || 'نامشخص'
                      }
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                      کد مراجع: {selectedClient._id || selectedClient.id}
                    </Text>
                  </VStack>
                </Flex>
                
                <Box w="100%">
                  <Text fontWeight="bold" mb={2}>آدرس:</Text>
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <FaMapMarkerAlt color="gray.400" />
                      <Text>آدرس: {selectedClient.address || 'ثبت نشده'}</Text>
                    </HStack>
                  </VStack>
                </Box>

                <Box w="100%">
                  <Text fontWeight="bold" mb={2}>اطلاعات شخصی:</Text>
                  <VStack align="start" spacing={2}>
                    <Text>شغل: {selectedClient.occupation || 'ثبت نشده'}</Text>
                    <Text>تاریخ تولد: {selectedClient.birthDate ? (
                      <JalaliDateDisplay
                        date={selectedClient.birthDate}
                        format="jYYYY/jMM/jDD"
                      />
                    ) : 'ثبت نشده'}</Text>
                    <Text>جنسیت: {selectedClient.gender === 'male' ? 'مرد' : selectedClient.gender === 'female' ? 'زن' : 'ثبت نشده'}</Text>
                  </VStack>
                </Box>

                {selectedClient.notes && (
                  <Box w="100%">
                    <Text fontWeight="bold" mb={2}>یادداشت‌ها:</Text>
                    <Text>{selectedClient.notes}</Text>
                  </Box>
                )}

                <Box w="100%">
                  <Button
                    colorScheme="teal"
                    w="100%"
                    onClick={() => {
                      onClose();
                      handleViewProfile(selectedClient._id || selectedClient.id);
                    }}
                  >
                    مشاهده پروفایل کامل
                  </Button>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default MyClients; 